import {
  Box,
  Grid,
  Typography,
  Theme,
  Hidden,
  IconButton,
  Button,
  LinearProgress,
  CardMedia,
} from '@mui/material';
import { SubHeading } from '../investors/components';
import React, { useEffect, useState } from 'react';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { useHistory, useRouteMatch } from 'react-router';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { Gridstyles, Item, ItemDetails } from '../ApplicationDetails';
import { useDispatch } from 'react-redux';
import { accessType, ClassPlanProps } from '../../redux-store/types/funds';
import { fundAccess, getPlanDetail } from '../../redux-store/actions/funds';
import { Location } from 'history';
import { formatShortDate } from '../../utils/date';
import { KeyValue } from '../ApplicationDetails/contributor';
import {
  currencyConversion,
  isFieldApplicable,
  isFieldVariable,
  isAccreditedInvestorApplicable,
} from '../../utils/utilityFunctions';
import {
  CurrencyMaster,
  defaultCurrency,
  FEE_TYPE_MASTER,
  YES_NO_BOOLEAN_MASTER,
  YES_NO_MASTER,
  ENABLE_PARENT_SHARE_CLASS,
  ENABLE_FAMILY_FOLIO,
} from '../../utils/constant';
import { styles } from '../../Styles/styles';

export default function PlanDetails({
  location,
}: {
  location: Location<{ PlanId: string; fundId: string }>;
}): JSX.Element {
  const history = useHistory();
  const dispatch = useDispatch();
  const [isLoading, setLoading] = useState(false);
  const [access, setAccess] = useState(false);
  const { PlanId, fundId } = location.state || {};
  const [planDetails, setPlanDetails] = useState<ClassPlanProps>();

  useEffect(() => {
    let isComponentActive = true;
    (async function () {
      try {
        setLoading(true);
        const { fundSetupAccess } = (await dispatch(fundAccess())) as unknown as accessType;
        if (PlanId) {
          const res = (await dispatch(getPlanDetail(PlanId))) as unknown as ClassPlanProps;
          setPlanDetails(res);
        }
        if (!isComponentActive) {
          return;
        }
        setAccess(fundSetupAccess);
        setLoading(false);
      } catch (e) {
        setLoading(false);
        console.error((e as Error).message);
      }
    })();
    return () => {
      isComponentActive = false;
    };
  }, []);

  return (
    <>
      <Box sx={styles.ApplicationDetailsCommonLayout}>
        <Box sx={{ p: { lg: 2, xs: 2 } }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: { lg: 'center', xs: 'flex-start' },
              flexDirection: { lg: 'row', xs: 'column' },
              justifyContent: 'space-between',
              ml: { lg: 7, xs: 2 },
              pt: { xs: 2, sm: 4 },
              mr: { lg: 7, xs: 2 },
              // pb: 1,
            }}>
            <Hidden smUp={true}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                  sx={{ height: 'fit-content', p: 0 }}
                  onClick={() => history.push('view-plans', { fundId: fundId, PlanId: PlanId })}>
                  <NavigateBeforeIcon fontSize="medium" sx={{ color: 'common.black' }} />
                </IconButton>
                <Typography
                  sx={{
                    fontSize: 20,
                    fontWeight: 600,
                    color: '#1C2D47',
                  }}>
                  Back
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  my: 2,
                  py: 2,
                  width: '100%',
                  pl: { xs: 3, sm: 0 },
                  color: 'primary.main',
                  bgcolor: 'rgba(238, 244, 251, 0.5)',
                }}>
                <PersonOutlineOutlinedIcon fontSize="large" />
                <Typography
                  sx={{
                    fontSize: 20,
                    fontWeight: 600,
                    pl: 1,
                    pr: 5,
                  }}>
                  Fund Setup
                </Typography>
              </Box>
            </Hidden>
            <Box sx={{ display: 'flex', alignItems: 'center', pb: 1 }}>
              <Hidden only="xs">
                <IconButton
                  sx={{ height: 'fit-content', p: 0 }}
                  onClick={() => history.push('view-plans', { fundId: fundId })}>
                  <NavigateBeforeIcon fontSize="medium" sx={{ color: 'common.black' }} />
                </IconButton>
              </Hidden>
              <Typography
                sx={{
                  fontSize: 20,
                  fontWeight: 600,
                  color: '#1C2D47',
                  pl: { xs: 3, sm: 0 },
                }}>
                {'Plan Details'}
              </Typography>
              <Hidden smUp={true}>
                {access && (
                  <IconButton sx={{ p: 0, ml: 12 }}>
                    <CardMedia
                      component="img"
                      src="/images/edit-icon-outlined.svg"
                      alt="Edit Icon"
                      sx={{ width: 'unset' }}
                      onClick={() => history.push(`edit-plan`, { PlanId: PlanId, fundId: fundId })}
                    />
                  </IconButton>
                )}
              </Hidden>
            </Box>
            <Hidden only="xs">
              {access && (
                <IconButton sx={{ p: 0, m: 0 }}>
                  <CardMedia
                    component="img"
                    src="/images/edit-icon-outlined.svg"
                    alt="Edit Icon"
                    sx={{ width: 'unset' }}
                    onClick={() => history.push(`edit-plan`, { PlanId: PlanId, fundId: fundId })}
                  />
                </IconButton>
              )}
            </Hidden>
          </Box>

          {isLoading && <LinearProgress sx={{ ml: 5, mr: 5 }} />}
          {planDetails && (
            <>
              <Box sx={{ mr: { lg: 20, xs: 4 } }}>
                <SubHeading
                  sx={{
                    color: 'common.blue',
                    letterSpacing: 0.5 + 'px',
                    padding: { xs: '10px 10px', sm: '10px 30px' },
                    ml: { lg: 8, xs: 2 },
                  }}>
                  {'Plan Details'}
                </SubHeading>
              </Box>

              <Grid container sx={{ mt: 1, pl: { xs: 2, sm: 10 }, pr: { xs: 2, sm: 10 }, pt: 1 }}>
                <React.Fragment>
                  <Gridstyles>
                    <Grid
                      item
                      container
                      pb={5}
                      rowSpacing={6}
                      columnSpacing={13}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start">
                      <KeyValue title={'Plan Code'} description={planDetails?.planCode} />
                      <KeyValue
                        title={'Plan Description'}
                        description={planDetails?.planDescription}
                      />
                      <KeyValue title={'Scheme Nature'} description={planDetails?.schemeNature} />
                      {/* <KeyValue
                        title={'Registration Number'}
                        description={planDetails?.registrationNumber}
                      /> */}
                      <KeyValue
                        title={'Scheme Category'}
                        description={planDetails?.schemeCategory}
                      />
                      <KeyValue title={'Country'} description={planDetails?.country} />
                      <KeyValue
                        title={'Currency'}
                        description={
                          planDetails?.currency
                            ? CurrencyMaster[
                                planDetails?.currency?.toLowerCase() || defaultCurrency
                              ]?.currency
                            : planDetails?.currency
                        }
                      />
                      <KeyValue
                        title={'Transcation Currency'}
                        description={
                          planDetails?.transactionCurrency
                            ? CurrencyMaster[
                                planDetails?.transactionCurrency?.toLowerCase() || defaultCurrency
                              ]?.currency
                            : planDetails?.transactionCurrency
                        }
                      />
                      <KeyValue
                        title={'Scheme Start Date'}
                        description={formatShortDate(planDetails?.schemeStartDate)}
                      />
                      <KeyValue
                        title={'Lock in Period(In years)'}
                        description={planDetails?.lockInPeriod}
                      />
                      <KeyValue
                        title={'Lock in Extension(In years)'}
                        description={planDetails?.lockInExtension}
                      />
                      <KeyValue title={'Face Value'} description={planDetails?.faceValue} />
                      <KeyValue title={'Fund Address 1'} description={planDetails?.fundAddress1} />
                      <KeyValue title={'Fund Address 2'} description={planDetails?.fundAddress2} />
                      <KeyValue title={'Fund Address 3'} description={planDetails?.fundAddress3} />
                      <KeyValue title={'Setup Fee'} description={planDetails?.setupFee} />
                      <KeyValue title={'Custodian'} description={planDetails?.custodian} />
                      <KeyValue
                        title={'Minimum Commitment Amount'}
                        description={planDetails?.minCommitmentAmount}
                        amount={currencyConversion(
                          planDetails?.minCommitmentAmount,
                          planDetails?.currency
                        )}
                      />
                      <KeyValue
                        title={'Maximum Commitment Amount'}
                        description={planDetails?.maxCommitmentAmount}
                        amount={currencyConversion(
                          planDetails?.maxCommitmentAmount,
                          planDetails?.currency
                        )}
                      />
                      {ENABLE_FAMILY_FOLIO && (
                        <KeyValue
                          title={'Group Investment Commitment Amount'}
                          description={planDetails?.minInvestmentAmt}
                          amount={currencyConversion(
                            planDetails?.minInvestmentAmt,
                            planDetails?.currency
                          )}
                        />
                      )}
                      <KeyValue
                        title={'Minimum Contribution Percentage'}
                        description={planDetails?.minContributionPercentage}
                      />
                      <KeyValue
                        title={'Accredited Investor Applicable'}
                        description={YES_NO_MASTER[planDetails?.accreditedInvestorApplicable]}
                      />
                      {isAccreditedInvestorApplicable(
                        planDetails?.accreditedInvestorApplicable
                      ) && (
                        <KeyValue
                          title={'Accredited Minimum Commitment Amount'}
                          description={planDetails?.accreditedMinCommitmentAmount}
                          amount={currencyConversion(
                            planDetails?.accreditedMinCommitmentAmount,
                            planDetails?.currency
                          )}
                        />
                      )}
                      <KeyValue
                        title={'Investment Manager Name'}
                        description={planDetails?.fundManager?.name}
                      />
                      <KeyValue title={'Trustee Name'} description={planDetails?.trustee?.name} />
                      <KeyValue title={'Management Fee'} description={planDetails?.managementFee} />
                      <KeyValue title={'T&C Link'} description={planDetails?.tcLink} />
                      <KeyValue title={'Fee Structure'} description={planDetails?.planClass} />
                      <KeyValue title={'Class of Units'} description={planDetails?.classOfUnits} />
                      <KeyValue
                        title={'Hurdle Rate Applicable'}
                        description={YES_NO_MASTER[planDetails?.hurdleRateApplicable || '']}
                      />
                      {isFieldApplicable(planDetails?.hurdleRateApplicable) && (
                        <>
                          <KeyValue
                            title={'Hurdle Rate Type'}
                            description={FEE_TYPE_MASTER[planDetails?.hurdleRateType || '']}
                          />
                          <KeyValue
                            title={'Hurdle Rate %'}
                            description={planDetails?.defaultHurdleRate}
                          />
                        </>
                      )}
                      {isFieldVariable(
                        planDetails?.hurdleRateType,
                        planDetails?.hurdleRateApplicable
                      ) && (
                        <>
                          <KeyValue
                            title={'Minimum Hurdle Rate %'}
                            description={planDetails?.minHurdleRate}
                          />
                          <KeyValue
                            title={'Maximum Hurdle Rate %'}
                            description={planDetails?.maxHurdleRate}
                          />
                        </>
                      )}
                      <KeyValue
                        title={'Performance Fee Applicable'}
                        description={YES_NO_MASTER[planDetails?.performanceFeeApplicable || '']}
                      />
                      {isFieldApplicable(planDetails?.performanceFeeApplicable) && (
                        <>
                          <KeyValue
                            title={'Performance Fee Type'}
                            description={FEE_TYPE_MASTER[planDetails?.performanceFeeType || '']}
                          />
                          <KeyValue
                            title={'Performance Fee %'}
                            description={planDetails?.defaultPerformanceFee}
                          />
                        </>
                      )}
                      {isFieldVariable(
                        planDetails?.performanceFeeType,
                        planDetails?.performanceFeeApplicable
                      ) && (
                        <>
                          <KeyValue
                            title={'Minimum Performance Fee %'}
                            description={planDetails?.minPerformanceFee}
                          />
                          <KeyValue
                            title={'Maximum Performance Fee %'}
                            description={planDetails?.maxPerformanceFee}
                          />
                        </>
                      )}
                      <KeyValue
                        title={'Additional Return Split Applicable'}
                        description={YES_NO_MASTER[planDetails?.addReturnSplitApplicable || '']}
                      />
                      {isFieldApplicable(planDetails?.addReturnSplitApplicable) && (
                        <>
                          <KeyValue
                            title={'Additional Return Split Type'}
                            description={FEE_TYPE_MASTER[planDetails?.addReturnSplitType || '']}
                          />
                          <KeyValue
                            title={'Additional Return Split %'}
                            description={planDetails?.defaultAddReturnSplit}
                          />
                        </>
                      )}
                      {isFieldVariable(
                        planDetails?.addReturnSplitType,
                        planDetails?.addReturnSplitApplicable
                      ) && (
                        <>
                          <KeyValue
                            title={'Minimum Additional Return Split %'}
                            description={planDetails?.minAddReturnSplit}
                          />
                          <KeyValue
                            title={'Maximum Additional Return Split %'}
                            description={planDetails?.maxAddReturnSplit}
                          />
                        </>
                      )}
                      {ENABLE_PARENT_SHARE_CLASS && (
                        <KeyValue
                          title={'Parent Share Class'}
                          description={planDetails?.parentShareClass}
                        />
                      )}
                      <KeyValue
                        title={'Direct'}
                        description={planDetails?.isApplicableForDirect ? 'Yes' : 'No'}
                      />
                      <KeyValue
                        title={'Distributor'}
                        description={planDetails?.isApplicableForDistributor ? 'Yes' : 'No'}
                      />
                      <SubHeading
                        sx={{
                          color: 'common.blue',
                          letterSpacing: 0.5 + 'px',
                          padding: { xs: '10px 10px', sm: '10px 30px' },
                          ml: { lg: 8, xs: 2 },
                        }}>
                        {'Top Up Plan Details'}
                      </SubHeading>
                      <KeyValue
                        title={'Top Up Applicable'}
                        description={
                          YES_NO_BOOLEAN_MASTER[planDetails?.isTopupApplicable?.toString() || '']
                        }
                      />
                      {planDetails?.isTopupApplicable && (
                        <>
                          <KeyValue
                            title={'Top Up Minimum Commitment Amount'}
                            description={planDetails?.topUpMinCommitmentAmount}
                            amount={currencyConversion(
                              planDetails?.topUpMinCommitmentAmount,
                              planDetails?.currency
                            )}
                          />
                          <KeyValue
                            title={'Top Up Maximum Commitment Amount'}
                            description={planDetails?.topUpMaxCommitmentAmount}
                            amount={currencyConversion(
                              planDetails?.topUpMaxCommitmentAmount,
                              planDetails?.currency
                            )}
                          />
                          <KeyValue
                            title={'Top up Group Investment Commitment Amount'}
                            description={
                              ENABLE_FAMILY_FOLIO ? planDetails?.topupMinInvestmentAmt : 'N/A'
                            }
                            amount={
                              ENABLE_FAMILY_FOLIO
                                ? currencyConversion(
                                    planDetails?.topupMinInvestmentAmt,
                                    planDetails?.currency
                                  )
                                : undefined
                            }
                          />
                          <KeyValue
                            title={'Minimum Contribution Percentage'}
                            description={planDetails?.topUpMinContributionPercentage}
                          />
                          <KeyValue
                            title={'Top Up Setup Fee and Operating Expenses Applicable'}
                            description={YES_NO_MASTER[planDetails?.topUpSetupFeeApplicable || '']}
                          />
                          {isFieldApplicable(planDetails?.topUpSetupFeeApplicable) && (
                            <>
                              <KeyValue
                                title={'Top Up Setup Fee and Operating Expenses Type'}
                                description={FEE_TYPE_MASTER[planDetails?.topUpSetupFeeType || '']}
                              />
                              <KeyValue
                                title={'Top Up Setup Fee and Operating Expenses %'}
                                description={planDetails?.topUpSetupFee}
                              />
                            </>
                          )}
                          {isFieldVariable(
                            planDetails?.topUpSetupFeeType,
                            planDetails?.topUpSetupFeeApplicable
                          ) && (
                            <>
                              <KeyValue
                                title={'Minimum Top Up Setup Fee and Operating Expenses %'}
                                description={planDetails?.topUpMinSetupFee}
                              />
                              <KeyValue
                                title={'Maximum Top Up Setup Fee and Operating Expenses %'}
                                description={planDetails?.topUpMaxSetupFee}
                              />
                            </>
                          )}
                          <KeyValue
                            title={'Top Up Accredited Investor Applicable'}
                            description={
                              YES_NO_MASTER[planDetails?.topUpAccreditedInvestorApplicable]
                            }
                          />
                          {isAccreditedInvestorApplicable(
                            planDetails?.topUpAccreditedInvestorApplicable
                          ) && (
                            <KeyValue
                              title={'Top Up Accredited Minimum Commitment Amount'}
                              description={planDetails?.topUpAccreditedMinCommitmentAmount}
                            />
                          )}
                          <KeyValue
                            title={'Top Up Management Fee Applicable'}
                            description={
                              YES_NO_MASTER[planDetails?.topUpManagementFeeApplicable || '']
                            }
                          />
                          {isFieldApplicable(planDetails?.topUpManagementFeeApplicable) && (
                            <>
                              <KeyValue
                                title={'Top Up Management Fee Type'}
                                description={
                                  FEE_TYPE_MASTER[planDetails?.topUpManagementFeeType || '']
                                }
                              />
                              <KeyValue
                                title={'Top Up Management Fee %'}
                                description={planDetails?.defaultTopUpManagementFee}
                              />
                            </>
                          )}
                          {isFieldVariable(
                            planDetails?.topUpManagementFeeType,
                            planDetails?.topUpManagementFeeApplicable
                          ) && (
                            <>
                              <KeyValue
                                title={'Minimum Top Up Management Fee %'}
                                description={planDetails?.topUpMinManagementFee}
                              />
                              <KeyValue
                                title={'Maximum Top Up Management Fee %'}
                                description={planDetails?.topUpMaxManagementFee}
                              />
                            </>
                          )}
                          <KeyValue
                            title={'Top Up Hurdle Rate Applicable'}
                            description={
                              YES_NO_MASTER[planDetails?.topUpHurdleRateApplicable || '']
                            }
                          />
                          {isFieldApplicable(planDetails?.topUpHurdleRateApplicable) && (
                            <>
                              <KeyValue
                                title={'Top Up Hurdle Rate Type'}
                                description={
                                  FEE_TYPE_MASTER[planDetails?.topUpHurdleRateType || '']
                                }
                              />
                              <KeyValue
                                title={'Top Up Hurdle Rate %'}
                                description={planDetails?.defaultTopUpHurdleRate}
                              />
                            </>
                          )}
                          {isFieldVariable(
                            planDetails?.topUpHurdleRateType,
                            planDetails?.topUpHurdleRateApplicable
                          ) && (
                            <>
                              <KeyValue
                                title={'Minimum Top Up Hurdle Rate %'}
                                description={planDetails?.topUpMinHurdleRate}
                              />
                              <KeyValue
                                title={'Maximum Top Up Hurdle Rate %'}
                                description={planDetails?.topUpMaxHurdleRate}
                              />
                            </>
                          )}
                          <KeyValue
                            title={'Top Up Performance Fee Applicable'}
                            description={
                              YES_NO_MASTER[planDetails?.topUpPerformanceFeeApplicable || '']
                            }
                          />
                          {isFieldApplicable(planDetails?.topUpPerformanceFeeApplicable) && (
                            <>
                              <KeyValue
                                title={'Top Up Performance Fee Type'}
                                description={
                                  FEE_TYPE_MASTER[planDetails?.topUpPerformanceFeeType || '']
                                }
                              />
                              <KeyValue
                                title={'Top Up Performance Fee %'}
                                description={planDetails?.defaultTopUpPerformanceFee}
                              />
                            </>
                          )}
                          {isFieldVariable(
                            planDetails?.topUpPerformanceFeeType,
                            planDetails?.topUpPerformanceFeeApplicable
                          ) && (
                            <>
                              <KeyValue
                                title={'Minimum Top Up Performance Fee %'}
                                description={planDetails?.topUpMinPerformanceFee}
                              />
                              <KeyValue
                                title={'Maximum Top Up Performance Fee %'}
                                description={planDetails?.topUpMaxPerformanceFee}
                              />
                            </>
                          )}
                          <KeyValue
                            title={'Top Up Exit Load Applicable'}
                            description={
                              YES_NO_MASTER[planDetails?.topUpExitChargeApplicable || '']
                            }
                          />
                          {isFieldApplicable(planDetails?.topUpExitChargeApplicable) && (
                            <>
                              <KeyValue
                                title={'Top up Exit Load Type'}
                                description={
                                  FEE_TYPE_MASTER[planDetails?.topUpExitChargeType || '']
                                }
                              />
                              <KeyValue
                                title={'Top up Exit Load %'}
                                description={planDetails?.defaultTopUpExitCharge}
                              />
                              <KeyValue
                                title={'Exit Load Applicable Till'}
                                description={planDetails?.topupExitChargeApplicableTill}
                              />
                            </>
                          )}
                          {isFieldVariable(
                            planDetails?.topUpExitChargeType,
                            planDetails?.topUpExitChargeApplicable
                          ) && (
                            <>
                              <KeyValue
                                title={'Minimum Top up Exit Load %'}
                                description={planDetails?.topUpMinExitCharge}
                              />
                              <KeyValue
                                title={'Maximum Top Up Exit Load %'}
                                description={planDetails?.topUpMaxExitCharge}
                              />
                            </>
                          )}
                          <KeyValue
                            title={'Top Up Placement Fee Applicable'}
                            description={
                              YES_NO_MASTER[planDetails?.topUpPlacementFeeApplicable || '']
                            }
                          />
                          {isFieldApplicable(planDetails?.topUpPlacementFeeApplicable) && (
                            <>
                              <KeyValue
                                title={'Top Up Placement Fee Type'}
                                description={
                                  FEE_TYPE_MASTER[planDetails?.topUpPlacementFeeType || '']
                                }
                              />
                              <KeyValue
                                title={'Top Up Placement Fee %'}
                                description={planDetails?.defaultTopUpPlacementFee}
                              />
                            </>
                          )}
                          {isFieldVariable(
                            planDetails?.topUpPlacementFeeType,
                            planDetails?.topUpPlacementFeeApplicable
                          ) && (
                            <>
                              <KeyValue
                                title={'Minimum Top Up Placement Fee %'}
                                description={planDetails?.topUpMinPlacementFee}
                              />
                              <KeyValue
                                title={'Maximum Top Up Placement Fee %'}
                                description={planDetails?.topUpMaxPlacementFee}
                              />
                            </>
                          )}
                          <KeyValue
                            title={'Top Up Operating Expense Applicable'}
                            description={
                              YES_NO_MASTER[planDetails?.topUpOperatingExpenseApplicable || '']
                            }
                          />
                          {isFieldApplicable(planDetails?.topUpOperatingExpenseApplicable) && (
                            <>
                              <KeyValue
                                title={'Top Up Operating Expense Type'}
                                description={
                                  FEE_TYPE_MASTER[planDetails?.topUpOperatingExpenseType || '']
                                }
                              />
                              <KeyValue
                                title={'Top Up Operating Expense %'}
                                description={planDetails?.defaultTopUpOperatingExpenses}
                              />
                            </>
                          )}
                          {isFieldVariable(
                            planDetails?.topUpOperatingExpenseType,
                            planDetails?.topUpOperatingExpenseApplicable
                          ) && (
                            <>
                              <KeyValue
                                title={'Minimum Top Up Operating Expense %'}
                                description={planDetails?.topUpMinOperatingExpense}
                              />
                              <KeyValue
                                title={'Maximum Operating Expense %'}
                                description={planDetails?.topUpMaxOperatingExpense}
                              />
                            </>
                          )}
                        </>
                      )}
                    </Grid>
                  </Gridstyles>
                </React.Fragment>
              </Grid>
            </>
          )}
        </Box>
      </Box>
    </>
  );
}
